// Public set-password page reached from an emailed manager invite link:
//   /accept-invite?token=…&email=…
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../services/api";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";

const ORANGE = "#FF6B00";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const email = params.get("email") || "";

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pwd !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/accept-invite", { token, email, new_password: pwd });
      setDone(true);
      toast.success("Password set! You can sign in now.");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set password");
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="accept-invite-invalid">
        <div className="bg-white border rounded-2xl p-8 max-w-md text-center">
          <h1 className="text-xl font-bold text-slate-800">Invalid invite link</h1>
          <p className="text-slate-500 mt-2">This link is missing required details. Ask your administrator to re-send the invite.</p>
          <Link to="/login" className="inline-block mt-4 text-orange-600 font-semibold">Go to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="accept-invite-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md w-full shadow-sm">
        {done ? (
          <div className="text-center" data-testid="accept-invite-done">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
            <h1 className="text-xl font-bold text-slate-800 mt-3">Password set!</h1>
            <p className="text-slate-500 mt-1">Redirecting you to sign in...</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="h-6 w-6" style={{ color: ORANGE }} />
              <h1 className="text-2xl font-bold text-slate-900">Activate your account</h1>
            </div>
            <p className="text-slate-500 text-sm mb-6">Set a password for <b>{email}</b> to finish onboarding.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">New password</label>
                <input data-testid="invite-pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                       className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 focus:border-orange-400 focus:outline-none"
                       placeholder="At least 8 characters" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Confirm password</label>
                <input data-testid="invite-pwd-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                       className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 focus:border-orange-400 focus:outline-none"
                       placeholder="Re-enter password" />
              </div>
              <button type="submit" disabled={loading} data-testid="invite-submit"
                      className="w-full text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2"
                      style={{ background: ORANGE }}>
                {loading ? <Loader2 className="animate-spin h-4 w-4" /> : "Set password & continue"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
